import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { PostcardFace } from "@/components/PostcardFace";
import { Footer } from "@/components/Footer";
import { ArrowsClockwise, MapPin } from "@phosphor-icons/react";
import { POSTCARD_IMG, LOGO_IMG } from "@/lib/brand";

export default function ConsumerGrid() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const [campaign, setCampaign] = useState(null);
  const [slots, setSlots] = useState([]);
  const [face, setFace] = useState(searchParams.get("face") === "back" ? "Back" : "Front");

  const loadSlots = () => api.get(`/campaigns/${campaignId}/slots`).then(({ data }) => setSlots(data));

  useEffect(() => {
    api.get(`/campaigns/${campaignId}`).then(({ data }) => setCampaign(data)).catch(() => {});
    loadSlots();
    // eslint-disable-next-line
  }, [campaignId]);

  const onRedeem = async (slot) => {
    try {
      const { data: t } = await api.get(`/slots/${slot.id}/redeem-token`);
      const { data } = await api.post(`/slots/${slot.id}/redeem`, { token: t.token, source: "consumer" });
      toast.success(`Offer redeemed at ${slot.business_name}! Show this to the merchant.`);
      setSlots((prev) => prev.map((s) => (s.id === slot.id ? { ...s, total_redemptions: data.total_redemptions, redemption_cap: data.redemption_cap ?? s.redemption_cap } : s)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not redeem right now.");
    }
  };

  const onShare = async (slot) => {
    await api.post(`/slots/${slot.id}/click`).catch(() => {});
    const url = window.location.href;
    const text = `Check out this deal from ${slot.business_name}: ${slot.offer_details}`;
    if (navigator.share) {
      navigator.share({ title: slot.business_name, text, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${text} — ${url}`);
      toast.success("Deal link copied — share it with a friend!");
    }
  };

  // log views once
  useEffect(() => {
    if (slots.length) {
      slots.filter((s) => s.status === "Reserved").forEach((s) => api.post(`/slots/${s.id}/view`).catch(() => {}));
    }
    // eslint-disable-next-line
  }, [campaignId, slots.length]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 p-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><img src={LOGO_IMG} alt="PMI" className="w-40 sm:w-[220px] h-auto" /></Link>
          {campaign && (
            <span className="hidden sm:flex items-center gap-2 text-sm text-slate-500">
              <MapPin size={16} className="text-amber-600" /> {campaign.label}
            </span>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 pt-12 pb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">The Virtual Postcard</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-2">Your Local Deal Grid</h1>
        <p className="text-slate-500 mt-3 max-w-xl">Flip between the front and back of your 9×12 postcard. Tap any offer to redeem or share.</p>

        <div className="mt-8 inline-flex border border-slate-300 p-1 bg-white" data-testid="face-toggle">
          {["Front", "Back"].map((f) => (
            <button
              key={f} onClick={() => setFace(f)} data-testid={`face-${f}`}
              className={`px-6 py-2 text-sm font-[600] transition-colors duration-200 flex items-center gap-2 ${
                face === f ? "bg-navy-900 text-white" : "text-slate-500 hover:text-navy-900"
              }`}
            >
              <ArrowsClockwise size={14} /> {f} Face
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-6">
        <div className="grid md:grid-cols-2 gap-6 items-center bg-white border border-slate-200 p-4 shadow-sm" data-testid="mailer-reference">
          <img src={(face === "Back" ? campaign?.back_image_id : campaign?.front_image_id) ? fileUrl(face === "Back" ? campaign.back_image_id : campaign.front_image_id) : POSTCARD_IMG} alt={`Printed 9x12 PMI postcard mailer (${face})`} className="w-full object-contain rounded-sm border border-slate-100" />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">The Physical Mailer · {face} Face</p>
            <h2 className="font-display text-2xl tracking-tight mt-1">Every deal maps to the printed 9×12 card</h2>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">The interactive grid below mirrors the exact 8-up front and back layout that lands in mailboxes across your neighborhood — tap any offer to redeem or share.</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-24">
        <div className="postcard-scene">
          <div className={`postcard-inner relative ${face === "Back" ? "is-flipped" : ""}`} data-testid="postcard-inner">
            <div className="postcard-face bg-white border-r border-b border-slate-300 shadow-xl" style={{ pointerEvents: face === "Front" ? "auto" : "none" }}>
              <PostcardFace face="Front" slots={slots} mode="consumer" onRedeem={onRedeem} onShare={onShare} />
            </div>
            <div className="postcard-face postcard-face-back bg-white border-r border-b border-slate-300 shadow-xl" style={{ pointerEvents: face === "Back" ? "auto" : "none" }}>
              <PostcardFace face="Back" slots={slots} mode="consumer" onRedeem={onRedeem} onShare={onShare} />
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">Tap a face above to flip the postcard.</p>
      </div>
      <Footer />
    </div>
  );
}
